import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { StatusType } from '../../model/input/status-type';
import { environment } from '../../environments/environment';
import { InputIconType } from '../../model/input/input-icon-type';
import { noop } from 'rxjs';
import { AutoCompleteType, FormsIds, InputType, SuffixType } from './form-input.config';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * The following service is here in order to be able to use the abilities of "ReactiveForms" in a component
 * * */
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => FormInputComponent),
  multi: true,
};

@Component({
  selector: 'dn-form-input',
  templateUrl: './form-input.component.html',
  styleUrl: './form-input.component.scss',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class FormInputComponent implements OnChanges, OnInit, OnDestroy {
  @Output() maxCharsExceeded: EventEmitter<any> = new EventEmitter();
  @Output() suffixClicked: EventEmitter<any> = new EventEmitter();
  @Input() set errorMessage(msg: string) {
    this.sanitizedErrorMessage = this.sanitizer.bypassSecurityTrustHtml(msg);
  }
  @Input() message = '';
  @Input() tooltipMessage = '';
  @Input() placeholder: string;
  @Input() hideError: boolean;
  @Input() inputId: string;
  @Input() inputType: InputType;
  @Input() formGroup: FormGroup;
  @Input() formControlName: string;
  @Input() formControl: FormControl;
  @Input() disabled: boolean;
  @Input() isInputVisible: boolean;
  @Input() maxCharsForSubmit: number;
  @Input() showValueStatus = true;
  @Input() showViewPassword = false;
  @Input() suffixType: SuffixType;
  @Input() suffixEntry: string;
  @Input() suffixShowOnHover: boolean;
  @Input() hideBrowserAutoComplete: boolean;
  @Input() showValidationIndicators = true;
  autoComplete: string = AutoCompleteType.ON;
  @Input() validationFunc = (val) => true;

  @Input() set iconType(name: string) {
    this.iconUrl = `${environment.icons_url}/${name}.png`;
    this._iconType = name;
  }

  inputIconType: typeof InputIconType;
  sanitizedErrorMessage: SafeHtml;
  _iconType;
  iconUrl: string;
  statusType;
  isInputValid = false;
  valueFilled = false;
  visibleUrl = `${environment.icons_url}/visible_icon.svg`;
  notVisibleUrl = `${environment.icons_url}/not-visible_icon.svg`;
  validIconUrl = `${environment.icons_url}/v_icon.svg`;
  inValidIconUrl = `${environment.icons_url}/x_icon.svg`;
  infoIconUrl = `${environment.icons_url}/info-icon.svg`;

  subs = [];
  /**
   * The following 3 lines are in order to be able to use the abilities of "ReactiveForms" in a component
   * * */
  private innerValue: any = '';
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  constructor(private readonly sanitizer: DomSanitizer) {
    this.statusType = StatusType;
    this.inputIconType = InputIconType;
  }

  ngOnInit(): void {
    const sub = this.subscribeToFromControl();
    if (sub) {
      this.subs.push(sub);
    }

    this.checkAutocomplete();
  }

  subscribeToFromControl(): any {
    if (!this.formGroup) {
      return null;
    }
    return this.formGroup.get(this.formControlName).valueChanges.subscribe((val) => {
      this.isInputValid = this.formGroup.get(this.formControlName).valid;
      this.valueFilled = val.length > 0 || this.formGroup.get(this.formControlName).touched;
    });
  }

  /**
   * Auto-submittingכ when the input fills "maxCharsForSubmit" number of chars
   * * */
  autoSubmit(): void {
    const formValue = this.formGroup?.value;
    const inputValue = formValue?.[this.formControlName] || '';
    const inputNumOfChars = inputValue.length;
    if (inputNumOfChars === this.maxCharsForSubmit) {
      this.maxCharsExceeded.emit();
    }
  }

  /**
   * toggling visibility of password input
   * */
  toggleVisible(): void {
    this.isInputVisible = !this.isInputVisible;
    if (this.isInputVisible) {
      this.inputType = InputType.Text;
    } else {
      this.inputType = InputType.Password;
    }
  }

  /**
   * The following lines are in order to be able to use the abilities of "ReactiveForms" in a component
   * * */
  // set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  // From ControlValueAccessor interface
  writeValue(value: any): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  suffixClickEvent(): void {
    this.suffixClicked.emit();
  }

  get showViewPasswordIcon(): boolean {
    return this.showViewPassword;
  }

  get showImgSuffix(): boolean {
    return this.suffixType && this.suffixType === SuffixType.Img;
  }
  get showTextSuffix(): boolean {
    return this.suffixType && this.suffixType === SuffixType.Text;
  }

  get passwordSuffixStateIcon(): string {
    return this.inputType !== InputType.Password ? this.notVisibleUrl : this.visibleUrl;
  }

  get buildSuffixImage(): string {
    return `${environment.icons_url}/${this.suffixEntry}.png`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled && this.formGroup && this.formControlName) {
      this.formGroup.get(this.formControlName).disable(changes.disabled.currentValue);
    }
  }

  private checkAutocomplete() {
    switch (this.inputId) {
      case FormsIds.EMAIL:
        this.autoComplete = AutoCompleteType.EMAIL;
        break;
      case FormsIds.NAME:
        this.autoComplete = AutoCompleteType.NAME;
        break;
      default:
        this.autoComplete = AutoCompleteType.ON;
        break;
    }
  }
}
